// @flow

import { type UserInputValidationError } from '@tectonics/prs/validation';
import { type BrazilianDocumentType } from '../types';
import { normalizeBrazilianDocument } from '../normalize-brazilian-document';

const CNPJ_PATTERN = /^[\dA-Z]{12}\d{2}$/u;
const CNPJ_REPEATED_DIGITS_PATTERN = /^(0{14}|1{14}|2{14}|3{14}|4{14}|5{14}|6{14}|7{14}|8{14}|9{14})$/u;
const CNPJ_FIRST_CHECK_DIGIT_POSITION = 12;
const CNPJ_CHECK_DIGITS_LENGTH = 2;

export function validateBrazilianDocument(document: string, documentType: BrazilianDocumentType): ?UserInputValidationError {
  if (documentType === 'cpf')
    return validateCpf(document);
  else
    return validateCnpj(document);
}

function validateCpf(document: string): ?UserInputValidationError {
  if (!document) {
    return { id: 'empty'};
  } else if (!/^\d{11}$/.test(document)) {
    return { id: 'invalid-cpf-pattern' };
  } else if (!validateCpfNumber(document)) {
    return { id: 'invalid-cpf-number' };
  }
}

function validateCnpj(document: string): ?UserInputValidationError {
  if (!CNPJ_PATTERN.test(normalizeBrazilianDocument(document))) {
    return { id: 'invalid-cnpj-pattern' };
  } else if (!validateCnpjNumber(document)) {
    return { id: 'invalid-cnpj-number' };
  }
}

export function validateCnpjNumber(number: string): boolean {
  if (typeof number !== 'string')
    return false;

  const cnpj = normalizeBrazilianDocument(number);

  if (!CNPJ_PATTERN.test(cnpj) || CNPJ_REPEATED_DIGITS_PATTERN.test(cnpj))
    return false;

  const checkDigits = cnpj.substring(CNPJ_FIRST_CHECK_DIGIT_POSITION);

  for (let digitIndex = 0; digitIndex < CNPJ_CHECK_DIGITS_LENGTH; digitIndex++) {
    const size = CNPJ_FIRST_CHECK_DIGIT_POSITION + digitIndex;

    if (calculateCnpjCheckDigit(cnpj.substring(0, size)) !== checkDigits.charAt(digitIndex))
      return false;
  }

  return true;
}

/**
 * Receita Federal rule for alphanumeric CNPJ: each character weighs its ASCII code minus 48,
 * so digits keep their numeric value and letters start at 17 ('A').
 */
function calculateCnpjCheckDigit(characters: string): string {
  let sum = 0;
  let weight = characters.length - 7;

  for (let position = 0; position < characters.length; position++) {
    sum += (characters.charCodeAt(position) - 48) * weight--;
    if (weight < 2) weight = 9;
  }

  return (sum % 11 < 2 ? 0 : 11 - (sum % 11)).toString();
}

// eslint-disable
export function validateCpfNumber(number: string): boolean {
  if (typeof number !== 'string')
    return false;

  // $FlowFixMe
  number = number.replace(/[^\d]/g, '');

  const r = /^(0{11}|1{11}|2{11}|3{11}|4{11}|5{11}|6{11}|7{11}|8{11}|9{11})$/;
  let sum = 0;
  let remainder;

  if (r.test(number)) return false;

  // $FlowFixMe
  for (let i = 1; i <= 9; i++) sum += (number.charAt(i - 1, i) * (11 - i));

  remainder = (sum * 10) % 11;

  if ((remainder === 10) || (remainder === 11)) remainder = 0;
  if (remainder.toString() !== number.charAt(9)) return false;

  sum = 0;
  // $FlowFixMe
  for (let i = 1; i <= 10; i++) sum += number.charAt(i - 1, i) * (12 - i);

  remainder = (sum * 10) % 11;

  if ((remainder === 10) || (remainder === 11)) remainder = 0;
  if (remainder.toString() !== number.charAt(10)) return false;

  return true;
}
